import React, { useContext, useEffect, useState } from "react";
import { IamWrapper } from "iam-client";
import {
  LogInFlowTerminationError,
  OnPasswordRequiredApi,
} from "iam-client/dist/wrapper/logInFlow";
import { OnTermsAndConditionsRetrievedApi } from "iam-client/dist/wrapper/shared";
import { UserContext } from "../../context/userContext";
import { ErrorContext } from "../error/provider";
import flowSteps from "./constants";
import Login from "./login";
import TermsOfService from "./terms-of-service";
import { LoadingContext } from "../../context/loaderContext";
import logging from "../../util/logging";
import { LanguageContext } from "../../context/languageContext";
import { ProductContext } from "../../context/product-context/productContext";
import { useNavigate } from "react-router-dom";
const log = logging("log-in flow");

export default function LoginRoute(): React.JSX.Element {
  const { language } = useContext(LanguageContext);
  const { fetchAuthenticatedUser } = useContext(UserContext);
  const { setOverlayActive } = useContext(LoadingContext);
  const { setError } = useContext(ErrorContext);
  const navigate = useNavigate();

  const [loginStep, setLoginStep] = React.useState<flowSteps>(
    flowSteps.LOGIN_REQUIRED,
  );
  const [requirePassword, setRequirePassword] =
    useState<OnPasswordRequiredApi>();
  const [onTermsAndConditionsRetrieved, setOnTermsAndConditionsRetrieved] =
    useState<OnTermsAndConditionsRetrievedApi>();

  const { setSelectedProduct } = useContext(ProductContext);

  useEffect(() => {
    const refreshTermsAndConditions = () => {
      if (
        onTermsAndConditionsRetrieved &&
        "retrieveTermsOfServices" in onTermsAndConditionsRetrieved
      ) {
        onTermsAndConditionsRetrieved.retrieveTermsOfServices();
      }
    };
    refreshTermsAndConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    IamWrapper.init({
      resolveGenericErrorHandlers: () => ({
        on5XX: (e: unknown) => {
          log.error(`Login: On Error ${JSON.stringify(e)}`);
          setError(e);
        },
      }),
    });

    const startLogIn = async () => {
      IamWrapper.logInFlow(
        {
          onInit: async ({ start }) => {
            await start();
          },
          onPasswordRequired: (params: OnPasswordRequiredApi) => {
            log.info("Login: On password required");
            setRequirePassword(params);
          },
          onTermsAndConditionsRetrieved: (
            params: OnTermsAndConditionsRetrievedApi,
          ) => {
            log.info("Login: On T&C retrieved");
            setOnTermsAndConditionsRetrieved(params);
            setLoginStep(flowSteps.T_AND_C_REQUIRED);
          },
          onError: (e: LogInFlowTerminationError[]) => {
            log.error(`Login: On Error ${JSON.stringify(e)}`);
            setError(e);
          },
          onFinish: () => {
            log.info("Login: On Finish");
            setSelectedProduct(null);
            fetchAuthenticatedUser();
            navigate("/");
          },
          onLoadingChange: (loading: boolean) => {
            setOverlayActive(loading);
            log.info("Login: Loading change " + loading);
          },
        },
        {
          applicationId: "1fa_commercial_onboarding",
        },
      );
    };
    startLogIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderComponent = () => {
    if (loginStep === flowSteps.LOGIN_REQUIRED) {
      return <Login onNext={requirePassword} />;
    }

    if (loginStep === flowSteps.T_AND_C_REQUIRED) {
      return <TermsOfService onNext={onTermsAndConditionsRetrieved} />;
    }

    return <Login onNext={requirePassword} />;
  };

  return renderComponent();
}
